import { useCallback, useEffect, useState } from "react";
import { api, errDetail, fmtEur } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { PiggyBank, Plus, CheckCircle2 } from "lucide-react";

const STAGES = ["idea", "identified", "validated", "in_progress", "realised", "cancelled"];
const STAGE_LABEL = { idea: "Idee", identified: "Identifiziert", validated: "Validiert", in_progress: "In Umsetzung", realised: "Realisiert", cancelled: "Abgebrochen" };
const STAGE_COLOR = { idea: "bg-gray-100 text-gray-600", identified: "bg-blue-100 text-blue-700", validated: "bg-indigo-100 text-indigo-700", in_progress: "bg-amber-100 text-amber-800", realised: "bg-green-100 text-green-700", cancelled: "bg-red-100 text-red-700" };
const CATS = ["Rohmaterial", "Verpackung", "Werkzeuge", "Logistik", "Dienstleistungen", "IT", "Instandhaltung", "Sonstiges"];

export default function Savings() {
  const [rows, setRows] = useState([]);
  const [dash, setDash] = useState(null);
  const [caps, setCaps] = useState([]);
  const [detail, setDetail] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const load = useCallback(() => {
    api.get("/srm/savings").then((r) => setRows(r.data || [])).catch((e) => toast.error(errDetail(e)));
    api.get("/srm/savings/dashboard").then((r) => setDash(r.data)).catch(() => {});
  }, []);
  useEffect(() => { api.get("/srm/my-caps").then((r) => setCaps(r.data.caps || [])).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);
  const canEdit = caps.includes("savings_edit") || caps.includes("srm_admin");

  return (
    <div className="p-6 max-w-[1300px] mx-auto" data-testid="savings-page">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-[#1B2A4A] flex items-center justify-center"><PiggyBank className="h-5 w-5 text-[#F5A623]" /></div>
          <div><h1 className="text-2xl font-bold text-[#1B2A4A]">Savings-Pipeline</h1><p className="text-sm text-gray-500">Stage-Gates · Finance-Sign-off · Target vs. Realised</p></div>
        </div>
        {canEdit && <Button data-testid="savings-new-btn" className="bg-[#1B2A4A] hover:bg-[#24365d]" onClick={() => setAddOpen(true)}><Plus className="h-4 w-4 mr-1" /> Neue Initiative</Button>}
      </div>

      {dash && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <div className="bg-white rounded-xl border border-gray-200 p-4" data-testid="savings-target"><div className="text-xs text-gray-500">Ziel (Target)</div><div className="text-xl font-bold text-[#1B2A4A]">{fmtEur(dash.total_target)}</div></div>
          <div className="bg-white rounded-xl border border-gray-200 p-4" data-testid="savings-realised"><div className="text-xs text-gray-500">Realisiert</div><div className="text-xl font-bold text-green-600">{fmtEur(dash.total_realised)}</div></div>
          <div className="bg-white rounded-xl border border-gray-200 p-4"><div className="text-xs text-gray-500">Quote</div><div className="text-xl font-bold text-gray-700">{dash.total_target ? ((dash.total_realised / dash.total_target) * 100).toFixed(0) : 0}%</div></div>
          <div className="bg-white rounded-xl border border-gray-200 p-4"><div className="text-xs text-gray-500">Initiativen</div><div className="text-xl font-bold text-[#1B2A4A]">{rows.length}</div></div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <Table>
          <TableHeader><TableRow><TableHead>Nummer</TableHead><TableHead>Titel</TableHead><TableHead>Kategorie</TableHead><TableHead>Owner</TableHead><TableHead className="text-right">Ziel</TableHead><TableHead className="text-right">Realisiert</TableHead><TableHead>Stufe</TableHead></TableRow></TableHeader>
          <TableBody>
            {rows.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-gray-400 py-8">Keine Initiativen</TableCell></TableRow>}
            {rows.map((r) => (
              <TableRow key={r.id} className="cursor-pointer hover:bg-gray-50" data-testid={`savings-row-${r.initiative_number}`} onClick={() => setDetail(r.id)}>
                <TableCell className="font-mono font-medium text-[#1B2A4A]">{r.initiative_number}</TableCell>
                <TableCell>{r.title}</TableCell><TableCell>{r.category}</TableCell><TableCell>{r.owner_name}</TableCell>
                <TableCell className="text-right">{fmtEur(r.target_amount)}</TableCell>
                <TableCell className="text-right text-green-600">{fmtEur(r.realised_amount)}</TableCell>
                <TableCell><Badge className={STAGE_COLOR[r.stage]}>{STAGE_LABEL[r.stage]}</Badge></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {addOpen && <NewSavingsDialog onClose={(ch) => { setAddOpen(false); if (ch) load(); }} />}
      {detail && <SavingsDetail id={detail} caps={caps} canEdit={canEdit} onClose={(ch) => { setDetail(null); if (ch) load(); }} />}
    </div>
  );
}

function NewSavingsDialog({ onClose }) {
  const [f, setF] = useState({ title: "", category: "Rohmaterial", target_amount: 0, baseline: { value: 0, source_period: "", note: "" } });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!f.title || !f.baseline.value || !f.baseline.source_period) { toast.error("Titel + Baseline (Wert & Quellperiode) erforderlich"); return; }
    setBusy(true);
    try { await api.post("/srm/savings", f); toast.success("Initiative angelegt"); onClose(true); }
    catch (e) { toast.error(errDetail(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={() => onClose(false)}>
      <DialogContent className="max-w-lg" data-testid="savings-new-dialog">
        <DialogHeader><DialogTitle>Neue Savings-Initiative</DialogTitle><DialogDescription>Eine Baseline ist verpflichtend.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><Label>Titel *</Label><Input data-testid="savings-title" value={f.title} onChange={(e) => setF((s) => ({ ...s, title: e.target.value }))} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Kategorie</Label>
              <Select value={f.category} onValueChange={(v) => setF((s) => ({ ...s, category: v }))}>
                <SelectTrigger data-testid="savings-category"><SelectValue /></SelectTrigger>
                <SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Zielbetrag (p.a.)</Label><Input type="number" data-testid="savings-target-input" value={f.target_amount} onChange={(e) => setF((s) => ({ ...s, target_amount: e.target.value }))} /></div>
          </div>
          <div className="border-t pt-2"><Label className="text-xs font-semibold">Baseline (Pflicht)</Label>
            <div className="grid grid-cols-2 gap-3 mt-1">
              <Input type="number" placeholder="Baseline-Wert" data-testid="baseline-value" value={f.baseline.value} onChange={(e) => setF((s) => ({ ...s, baseline: { ...s.baseline, value: e.target.value } }))} />
              <Input placeholder="Quellperiode (z.B. 2025)" data-testid="baseline-period" value={f.baseline.source_period} onChange={(e) => setF((s) => ({ ...s, baseline: { ...s.baseline, source_period: e.target.value } }))} />
            </div>
            <Textarea className="mt-2" placeholder="Baseline-Notiz" value={f.baseline.note} onChange={(e) => setF((s) => ({ ...s, baseline: { ...s.baseline, note: e.target.value } }))} />
          </div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => onClose(false)}>Abbrechen</Button>
          <Button data-testid="savings-save-btn" disabled={busy} className="bg-[#1B2A4A] hover:bg-[#24365d]" onClick={save}>Anlegen</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SavingsDetail({ id, caps, canEdit, onClose }) {
  const [i, setI] = useState(null);
  const [busy, setBusy] = useState(false);
  const [realised, setRealised] = useState("");
  const [mnote, setMnote] = useState("");
  const load = useCallback(() => { api.get(`/srm/savings/${id}`).then((r) => setI(r.data)).catch((e) => toast.error(errDetail(e))); }, [id]);
  useEffect(() => { load(); }, [load]);
  const isFinance = caps.includes("srm_admin");
  const act = async (fn, msg) => { setBusy(true); try { await fn(); toast.success(msg); load(); } catch (e) { toast.error(errDetail(e)); } finally { setBusy(false); } };
  const advance = (stage, extra = {}) => act(() => api.post(`/srm/savings/${id}/advance`, { stage, ...extra }), `Stufe: ${STAGE_LABEL[stage]}`);
  const signoff = () => act(() => api.post(`/srm/savings/${id}/finance-signoff`, {}), "Finance-Sign-off erteilt");
  if (!i) return null;
  const idx = STAGES.indexOf(i.stage);
  const nextStage = idx >= 0 && idx < 3 ? STAGES[idx + 1] : null;
  return (
    <Dialog open onOpenChange={() => onClose(false)}>
      <DialogContent className="max-w-lg" data-testid="savings-detail-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2 font-mono">{i.initiative_number} <Badge className={STAGE_COLOR[i.stage]}>{STAGE_LABEL[i.stage]}</Badge></DialogTitle>
          <DialogDescription>{i.title} · {i.category} · {i.owner_name}</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="bg-gray-50 rounded-lg p-2"><div className="text-xs text-gray-500">Baseline</div><div className="font-bold text-gray-700">{fmtEur(i.baseline.value)}</div><div className="text-xs text-gray-400">{i.baseline.source_period}</div></div>
            <div className="bg-gray-50 rounded-lg p-2"><div className="text-xs text-gray-500">Ziel</div><div className="font-bold text-[#1B2A4A]">{fmtEur(i.target_amount)}</div></div>
            <div className="bg-gray-50 rounded-lg p-2"><div className="text-xs text-gray-500">Realisiert</div><div className="font-bold text-green-600">{fmtEur(i.realised_amount)}</div></div>
          </div>
          {i.finance_signoff?.signed ? (
            <div className="rounded-lg bg-green-50 border border-green-200 p-2 text-sm text-green-800"><CheckCircle2 className="h-4 w-4 inline mr-1" /> Finance-Sign-off durch {i.finance_signoff.by}</div>
          ) : <div className="rounded-lg bg-amber-50 border border-amber-200 p-2 text-sm text-amber-800">Finance-Sign-off ausstehend (nötig vor „Realisiert")</div>}
          {i.measurement_note && <div className="text-sm text-gray-500">Messung: {i.measurement_note}</div>}

          {canEdit && i.stage !== "realised" && i.stage !== "cancelled" && (
            <div className="border-t pt-3 space-y-2">
              {nextStage && <Button size="sm" data-testid="savings-advance-btn" disabled={busy} onClick={() => advance(nextStage)}>Weiter zu „{STAGE_LABEL[nextStage]}"</Button>}
              {isFinance && !i.finance_signoff?.signed && <Button size="sm" variant="outline" data-testid="savings-signoff-btn" disabled={busy} onClick={signoff}>Finance-Sign-off erteilen</Button>}
              {i.stage === "in_progress" && (
                <div className="space-y-2 bg-gray-50 rounded-lg p-2">
                  <Label className="text-xs">Realisieren (an Ist-Rechnungspreisen)</Label>
                  <Input type="number" placeholder="Realisierter Betrag" data-testid="savings-realised-input" value={realised} onChange={(e) => setRealised(e.target.value)} />
                  <Input placeholder="Messnotiz" data-testid="savings-measurement-note" value={mnote} onChange={(e) => setMnote(e.target.value)} />
                  <Button size="sm" className="bg-green-600 hover:bg-green-700" data-testid="savings-realise-btn" disabled={busy || !realised || !mnote} onClick={() => advance("realised", { realised_amount: Number(realised), measurement_note: mnote })}>Als „Realisiert" buchen</Button>
                </div>
              )}
            </div>
          )}
        </div>
        <DialogFooter><Button variant="outline" onClick={() => onClose(false)}>Schließen</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
